<template>
    <div class="open-bills-list">
        <div class="loading-placeholder" v-if="isLoading">
            {{ translations.openBills.loadingData }}
        </div>
        <div class="error-placeholder" v-if="hasError" v-html="errorMessage"></div>
        <div class="no-open-bills-placeholder" v-if="!isLoading && !hasError && openBills.length == 0">
            {{ translations.openBills.noOpenBills }}
        </div>

        <div
            class="open-bills-table tabulator"
            ref="tableRef"
            :class="{ visible: !isLoading && openBills.length }"
        ></div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import type {
        OpenBillRoomSelectedEventData,
        OpenBillSelectedEventData,
        OpenBillTableData,
    } from "@/data/model/Bill";
    import { BillType } from "@/data/model/Enums";
    import { BillListType } from "@/data/model/Enums";
    import type { IOpenBill } from "@/data/model/Operator";
    import { useBillStore } from "@/data/stores/bill";
    import { useTableAllocationStore } from "@/data/stores/tableAllocation";
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoney } from "@/shared/utils/format";
    import { type CellComponent, type ColumnDefinition, TabulatorFull } from "tabulator-tables";
    import { computed, nextTick, onMounted, ref, watch } from "vue";

    const tableAllocation = useTableAllocationStore();

    // define properties
    const props = defineProps<{
        isMobileView: boolean;
        disableCurrentTable: boolean;
        billListType: BillListType;
    }>();

    // define emits
    const emit = defineEmits(["tableSelected", "roomSelected", "openBillsLoadingError"]);

    // state
    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const openBills = ref<IOpenBill[]>([]);
    const isLoading = ref(false);

    const tabulator = ref<TabulatorFull | null>(null);
    const tableRef = ref<HTMLElement | null>(null);

    const hasError = ref(false);
    const errorMessage = ref("");
    const bill = useBillStore();

    const openBillsCount = computed(() => openBills.value.length);

    // computed
    const mappedData = computed(() => {
        if (isLoading.value) {
            return [];
        }

        return openBills.value.map((openBill) => {
            return {
                name: openBill.name,
                locationId: openBill.table.locationId,
                roomId: openBill.table.roomId,
                roomName: openBill.table.roomName,
                tableId: openBill.table.tableId,
                tableNumber: openBill.table.tableNumber,
                tableName: openBill.table.tableName,
                operatorName: openBill.operatorName,
                occupancy: `${openBill.allocatedGuests} / ${openBill.table.seats}`,
                since: DateTime.toTimeString(openBill.openSince, false),
                due:
                    openBill.latestPreorderDateTime != null
                        ? DateTime.toDateTimeString(openBill.latestPreorderDateTime!)
                        : null,
                open: toMoney(openBill.openBillAmount),
                isDisabled: props.disableCurrentTable
                    ? isCurrentTableAndBill(openBill.table.tableId, openBill.billId, openBill.billType)
                    : false,
                bill: {
                    id: openBill.billId,
                    type: openBill.billType,
                    customName: openBill.billCustomName,
                },
            } as OpenBillTableData;
        });
    });

    // functions
    function isCurrentTableAndBill(tableId: string, billId: string, billType: BillType) {
        if (billType == BillType.TableBill) {
            if (bill.currentBill == null) {
                return tableAllocation.allocation?.tableId === tableId;
            } else {
                return tableAllocation.allocation?.tableId === tableId && billId == bill.currentBill?.id;
            }
        } else {
            if (bill.currentBill == null) {
                return false;
            } else {
                return tableAllocation.allocation?.tableId === tableId && billId == bill.currentBill?.id;
            }
        }
    }

    function initDataTable() {
        const isExtendedView = !props.isMobileView;
        let columns = [] as ColumnDefinition[];

        if (isExtendedView) {
            // Table column
            columns.push({
                title:
                    props.billListType == BillListType.OrdersForNowOnly
                        ? translations.openBills.table
                        : translations.openBills.bill,
                field: "name",
                headerSort: false,
                resizable: false,
                widthGrow: 1.5,
                cssClass: "table-cell",
                formatter: formatter,
            });

            // Room column - only for extended view
            columns.push({
                title: translations.openBills.room,
                field: "roomName",
                headerSort: false,
                resizable: false,
                widthGrow: 1.5,
                cssClass: "room-cell",
                formatter: formatter,
            });

            // Operator column - only for extended view
            columns.push({
                title: translations.openBills.operator,
                field: "operatorName",
                headerSort: false,
                resizable: false,
                widthGrow: 1.5,
                formatter: formatter,
            });

            // Occupancy column
            columns.push({
                title: translations.openBills.occupancy,
                field: "occupancy",
                headerSort: false,
                resizable: false,
                minWidth: 110,
                widthGrow: 0.5,
                cssClass: "align-right",
                formatter: formatter,
            });

            if (props.billListType == BillListType.OrdersForNowOnly) {
                // Since column
                columns.push({
                    title: translations.openBills.since,
                    field: "since",
                    headerSort: false,
                    resizable: false,
                    minWidth: 77,
                    widthGrow: 0.5,
                    cssClass: "align-right",
                    formatter: formatter,
                });
            } else {
                // Due column
                columns.push({
                    title: translations.openBills.due,
                    field: "due",
                    headerSort: false,
                    resizable: false,
                    minWidth: 160,
                    widthGrow: 0.5,
                    cssClass: "align-right",
                    formatter: formatter,
                });
            }

            // Open column
            columns.push({
                title: translations.openBills.open,
                field: "open",
                headerSort: false,
                resizable: false,
                cssClass: "align-right",
                widthGrow: 0.5,
                minWidth: 110,
                formatter: formatter,
            });
        } else {
            // Table column
            columns.push({
                title: translations.openBills.table,
                field: "name",
                headerSort: false,
                resizable: false,
                widthGrow: 1.5,
                cssClass: "table-cell",
                formatter: formatter,
            });

            // Occupancy column
            columns.push({
                title: translations.openBills.occupancy,
                field: "occupancy",
                headerSort: false,
                resizable: false,
                maxWidth: 110,
                cssClass: "align-right",
                formatter: formatter,
            });

            // Since column
            columns.push({
                title: translations.openBills.since,
                field: "since",
                headerSort: false,
                resizable: false,
                width: 77,
                cssClass: "align-right",
                formatter: formatter,
            });

            // Open column
            columns.push({
                title: translations.openBills.open,
                field: "open",
                headerSort: false,
                resizable: false,
                cssClass: "align-right",
                maxWidth: 110,
                formatter: formatter,
            });
        }

        tabulator.value = new TabulatorFull(tableRef.value!, {
            layout: "fitColumns",
            data: mappedData.value,
            // reactiveData: true, // this option doesn't work with Vue, maybe because of mismatched versions between imported libraries vs npm library for Tabulator
            // updates done in watcher instead
            columns: columns,
        });

        tabulator.value.on("cellClick", function (e, cell) {
            if (!isRoomCellClicked(e)) {
                e.preventDefault();
                return;
            }

            const rowData = cell.getRow().getData() as OpenBillTableData;

            emit("roomSelected", {
                locationId: rowData.locationId,
                roomId: rowData.roomId,
            } as OpenBillRoomSelectedEventData);
        });

        tabulator.value.on("rowClick", (e, row) => {
            // rowClick is triggered before cellClick, so we need to check if the cell is clickable
            if (isRoomCellClicked(e)) {
                e.preventDefault();
                return;
            }

            const rowData = row.getData() as OpenBillTableData;

            // if row is disabled - skip event
            if (rowData.isDisabled) {
                e.preventDefault();
                return;
            }

            emit("tableSelected", {
                locationId: rowData.locationId,
                roomId: rowData.roomId,
                tableId: rowData.tableId,
                tableNumber: rowData.tableNumber,
                selectedOpenBill: rowData.bill,
            } as OpenBillSelectedEventData);
        });

        function isRoomCellClicked(e: UIEvent) {
            const target = e.target as HTMLElement;
            return target.classList.contains("room-cell");
        }

        function formatter(cell: CellComponent) {
            // disable row if table row represents current table
            const rowHtmlElement = cell.getRow().getElement();

            const data = cell.getData() as OpenBillTableData;
            if (data.isDisabled) {
                rowHtmlElement.classList.add("disabled");
            } else {
                rowHtmlElement.classList.remove("disabled");
            }

            return cell.getValue();
        }
    }

    async function refresh() {
        hasError.value = false;
        try {
            openBills.value = await operatorDao.getOpenBills({
                includeLastOperatorName: !props.isMobileView,
                billListType: props.billListType,
            });
        } catch (ex) {
            logError(ex);
            hasError.value = true;
            errorMessage.value = translations.openBills.failedLoadingBills;
            emit("openBillsLoadingError");
        } finally {
            isLoading.value = false;
        }
    }

    async function loadOpenBillsData() {
        isLoading.value = true;
        try {
            await refresh();
        } finally {
            isLoading.value = false;
        }
    }

    async function redraw() {
        tabulator.value?.setData([]);
        await nextTick();
        tabulator.value?.setData(mappedData.value);
    }

    watch(mappedData, (newValue) => {
        tabulator.value?.setData(newValue);
    });

    // when the component is mounted (shown), get the open bills
    onMounted(async () => {
        await loadOpenBillsData();

        //instantiate Tabulator when element is mounted
        initDataTable();
    });

    // define exposed
    defineExpose({
        refresh,
        redraw,
        openBillsCount,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .open-bills-list {
        font-size: 14px;

        .open-bills-table {
            opacity: 0;
            // do not add transition, because it breaks the tabulator header

            &.visible {
                opacity: 1;
            }

            border-left: 1px solid #dee2e6;
            border-right: 1px solid #dee2e6;
        }

        .tabulator-header {
            font-weight: 100;

            .tabulator-headers {
                > .tabulator-col:last-child {
                    border-right: none;
                }
            }
        }

        .tabulator-table {
            .tabulator-row {
                transition: background-color 0.9s variables.$easeOutExpo;
                font-weight: 600;

                &.disabled {
                    color: #ccc;
                    cursor: default;

                    &.tabulator-row-odd {
                        background-color: #fff;
                    }

                    &.tabulator-row-even {
                        background-color: #f9f9f9;
                    }
                }

                &:not(.disabled) {
                    .tabulator-cell.room-cell,
                    .tabulator-cell.table-cell {
                        color: colors.$green;
                    }
                }
            }

            .tabulator-cell.align-right {
                text-align: right;
            }
        }

        .loading-placeholder,
        .error-placeholder,
        .no-open-bills-placeholder {
            text-align: center;
            padding: 1.5em 2em;
            line-height: 2em;
        }
    }
</style>
